import React, { useEffect, useState } from 'react';
import { ownerApi } from '../../lib/ownerApi';
import { Users, DollarSign, Beaker, AlertTriangle, Activity, Crown, TrendingUp, FileWarning } from 'lucide-react';
import { Link } from 'react-router-dom';

function StatCard({ label, value, icon: Icon, accent = 'slate', sub, testId }) {
  const accents = {
    slate: 'orka-text-secondary',
    emerald: 'text-emerald-700 dark:text-emerald-400',
    amber: 'text-amber-700 dark:text-amber-400',
    rose: 'text-rose-700 dark:text-rose-400',
    violet: 'text-violet-700 dark:text-violet-400',
    cyan: 'text-cyan-700 dark:text-cyan-400',
  };
  return (
    <div
      className="rounded-2xl border border orka-border-subtle orka-surface-raised p-4 shadow-sm"
      data-testid={testId}
    >
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-wider orka-text-tertiary font-semibold">{label}</div>
        {Icon && <Icon className={`w-4 h-4 ${accents[accent]}`} />}
      </div>
      <div className={`mt-2 text-3xl font-bold ${accents[accent]} font-mono tabular-nums`}>{value}</div>
      {sub && <div className="mt-1 text-xs orka-text-tertiary">{sub}</div>}
    </div>
  );
}

function Skeleton({ h = 'h-24' }) {
  return <div className={`rounded-2xl orka-surface-subtle animate-pulse ${h}`} />;
}

export default function OwnerOverviewPage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    ownerApi.overview().then(setData).catch(e => setError(e.message));
  }, []);

  if (error) {
    return (
      <div className="rounded-2xl border border-rose-200 bg-rose-50 dark:bg-rose-950/30 dark:border-rose-900 p-4 text-rose-800 dark:text-rose-300 text-sm" data-testid="owner-overview-error">
        Failed to load overview: {error}
      </div>
    );
  }

  if (!data) {
    return (
      <div className="space-y-4" data-testid="owner-overview-loading">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[0, 1, 2, 3].map(i => <Skeleton key={i} />)}
        </div>
        <Skeleton h="h-48" />
      </div>
    );
  }

  const t = data.totals || {};
  const c = data.commercial || {};
  const w = data.warnings || {};
  const r = data.recent || {};

  return (
    <div className="space-y-6" data-testid="owner-overview">
      {/* Top: commercial */}
      <div>
        <h2 className="text-sm font-semibold uppercase tracking-wider text-stone-500 mb-3">Commercial</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="MRR" value={`$${(c.mrr_usd ?? 0).toLocaleString()}`} icon={DollarSign} accent="emerald" sub="active paid subscriptions" testId="owner-stat-mrr" />
          <StatCard label="Paid Active" value={c.paid_active ?? 0} icon={Crown} accent="violet" testId="owner-stat-paid" />
          <StatCard label="Trialing" value={c.trialing ?? 0} icon={TrendingUp} accent="cyan" testId="owner-stat-trial" />
          <StatCard label="Free Users" value={c.free_users ?? 0} icon={Users} accent="slate" testId="owner-stat-free" />
        </div>
      </div>

      {/* Middle: warnings */}
      <div>
        <h2 className="text-sm font-semibold uppercase tracking-wider text-stone-500 mb-3">Warnings</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Past Due" value={c.past_due ?? 0} icon={AlertTriangle} accent={c.past_due > 0 ? 'rose' : 'slate'} testId="owner-stat-past-due" />
          <StatCard label="Cancelled" value={c.cancelled ?? 0} icon={AlertTriangle} accent={c.cancelled > 0 ? 'amber' : 'slate'} testId="owner-stat-cancelled" />
          <StatCard label="Near Usage Limit" value={w.near_limit_count ?? 0} icon={Activity} accent={w.near_limit_count > 0 ? 'amber' : 'slate'} testId="owner-stat-near-limit" />
          <StatCard label="Open Audit Findings" value={w.open_audit_findings ?? 0} icon={FileWarning} accent={w.open_audit_findings > 0 ? 'rose' : 'slate'} testId="owner-stat-audit" />
        </div>
      </div>

      {/* Lower: identity / demo */}
      <div>
        <h2 className="text-sm font-semibold uppercase tracking-wider text-stone-500 mb-3">Identity & Demo</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Persons" value={t.persons ?? 0} icon={Users} accent="slate" sub="canonical identity records" testId="owner-stat-persons" />
          <StatCard label="Users" value={t.users ?? 0} icon={Users} accent="slate" sub="auth records" testId="owner-stat-users" />
          <StatCard label="Subscriptions" value={t.subscriptions ?? 0} icon={DollarSign} accent="slate" testId="owner-stat-subs" />
          <StatCard label="Demo Accounts" value={c.demo_accounts ?? 0} icon={Beaker} accent="cyan" testId="owner-stat-demo" />
        </div>
      </div>

      {/* Recent activity */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="rounded-2xl border border orka-border-subtle orka-surface-raised p-4" data-testid="owner-recent-events">
          <h3 className="text-sm font-semibold mb-3 flex items-center gap-2"><Activity className="w-4 h-4" /> Recent Owner Actions</h3>
          {(r.account_events || []).length === 0 ? (
            <div className="text-xs text-stone-400 italic py-4">No recent actions yet — owner actions will appear here.</div>
          ) : (
            <ul className="space-y-2">
              {r.account_events.slice(0, 8).map(e => (
                <li key={e.event_id} className="text-xs flex items-start gap-2 border-b border-stone-100 dark:border-slate-800 pb-1.5 last:border-0">
                  <span className="font-mono text-stone-400 shrink-0">{(e.created_at || '').slice(11, 16)}</span>
                  <span className="font-medium orka-text-primary">{e.event_type}</span>
                  <span className="text-stone-500 truncate">{e.actor_email}</span>
                  {e.risk_level && e.risk_level !== 'low' && (
                    <span className={`ml-auto text-[9px] px-1.5 py-0.5 rounded-full uppercase ${
                      e.risk_level === 'critical' ? 'bg-rose-100 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300' :
                      e.risk_level === 'high' ? 'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300' :
                      'bg-stone-100 text-stone-600 dark:bg-slate-800 dark:text-stone-400'
                    }`}>{e.risk_level}</span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="rounded-2xl border border orka-border-subtle orka-surface-raised p-4" data-testid="owner-recent-billing">
          <h3 className="text-sm font-semibold mb-3 flex items-center gap-2"><DollarSign className="w-4 h-4" /> Recent Stripe Events</h3>
          {(r.billing_events || []).length === 0 ? (
            <div className="text-xs text-stone-400 italic py-4">No recent webhook events yet.</div>
          ) : (
            <ul className="space-y-2">
              {r.billing_events.slice(0, 8).map(e => (
                <li key={e.billing_event_id} className="text-xs flex items-start gap-2 border-b border-stone-100 dark:border-slate-800 pb-1.5 last:border-0">
                  <span className="font-mono text-stone-400 shrink-0">{(e.created_at || '').slice(11, 16)}</span>
                  <span className="font-mono orka-text-primary">{e.event_type}</span>
                  <span className={`ml-auto text-[9px] px-1.5 py-0.5 rounded-full ${
                    e.processed ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300' : 'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300'
                  }`}>{e.processed ? 'processed' : 'pending'}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="text-xs orka-text-disabled">
        Tip: jump straight into <Link to="/owner/accounts" className="text-violet-600 dark:text-violet-400 underline">Accounts</Link> or <Link to="/owner/people" className="text-violet-600 dark:text-violet-400 underline">People & Users</Link> to inspect a customer.
      </div>
    </div>
  );
}
